/**
 * React.lazy that survives a deploy.
 *
 * Vite gives every chunk a content hash, and a deploy replaces those files. A
 * tab that was open beforehand still holds the OLD index, so the first time the
 * agent navigates to a route they have not visited yet, the browser asks for a
 * filename that no longer exists:
 *
 *   Error: Unable to preload CSS for /assets/UnifiedActivityTimeline-CpXNZsb8.css
 *
 * That rejection reaches the error boundary and the agent gets a broken screen
 * for a reason that has nothing to do with them. It is guaranteed to happen to
 * every customer on every deploy that touches a lazily-loaded route.
 *
 * The fix is to reload once, which fetches the new index and the new chunk
 * names. Two things keep this honest rather than a fallback that hides a
 * problem:
 *
 *  - Only chunk-loading failures are retried. Any other error from the module
 *    is rethrown untouched, so a genuinely broken page still breaks loudly.
 *  - The reload happens at most once per session. If the chunk is still missing
 *    afterwards, the file really is absent and the error is rethrown rather
 *    than the tab reloading forever.
 */

import { lazy, type ComponentType } from 'react'

const RELOAD_KEY = 'reppr.chunk-reload'

/** The messages browsers use when a hashed chunk is gone. */
function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return (
    message.includes('Unable to preload CSS') ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Importing a module script failed')
  )
}

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const module = await factory()
      // Loaded cleanly, so any earlier reload did its job.
      try { sessionStorage.removeItem(RELOAD_KEY) } catch { /* private mode */ }
      return module
    } catch (error) {
      if (!isChunkLoadError(error)) throw error

      let alreadyReloaded = false
      try {
        alreadyReloaded = sessionStorage.getItem(RELOAD_KEY) === '1'
        if (!alreadyReloaded) sessionStorage.setItem(RELOAD_KEY, '1')
      } catch {
        // Storage blocked: reloading could loop, so surface the error instead.
        throw error
      }

      if (alreadyReloaded) throw error

      window.location.reload()
      // The reload takes over; never resolve, so nothing renders in the
      // meantime and the error boundary is not triggered on the way out.
      return new Promise<never>(() => {})
    }
  })
}
